import {ButtonProps, closeCurrentDialog, openDialog, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useDispatch} from 'react-redux';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {documentContextApi} from '@dms/api/core';
import {
  BaseDirectSaleMaterialItem,
  GetDirectSaleMaterialItemResponse,
  GetWarehousesResponse,
  useBulkDeleteDirectSaleItemsMutation,
  useDeleteDirectSaleMaterialBasketItemsMutation,
  useGetDirectSaleMaterialBasketItemsQuery,
  useLazyGetDirectSaleMaterialItemQuery,
  usePatchDirectSaleMaterialBasketItemQuantityMutation,
  usePatchDirectSaleMaterialItemMutation,
  usePostDirectSaleMaterialBasketCheckoutMutation,
  usePostDirectSaleMaterialDispatchMutation,
  usePostDirectSaleMaterialNonBindingMutation,
  usePostDirectSaleMaterialRequestMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  logError,
  MaterialBasketItemEditingDetails,
  useInvalidBasketItemsIds,
} from '@dms/shared';
import {useBasketMechanic} from '@dms/shared-aftersales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {RequestTabMaterialItemEdit} from './RequestTabMaterialItemEdit';
import {RequestTabMaterialList} from './RequestTabMaterialList';

interface RequestTabMaterialModalProps extends RequiredTestIdProps {
  directSaleId: string;
  authorizationProfileId: string | Nullish;
  onDiscard: () => void;
  availableWarehouses: GetWarehousesResponse;
}

export function RequestTabMaterialModal(props: RequestTabMaterialModalProps) {
  const dispatch = useDispatch();
  const [getDirectSaleMaterialItem] = useLazyGetDirectSaleMaterialItemQuery();

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetDirectSaleMaterialBasketItemsQuery({directSaleId: props.directSaleId});

  const [checkoutMaterialBasket, {isLoading: isCheckingOutBasket}] =
    usePostDirectSaleMaterialBasketCheckoutMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] =
    useDeleteDirectSaleMaterialBasketItemsMutation();

  const [dispatchMaterial, dispatchResult] = usePostDirectSaleMaterialDispatchMutation();

  const [requestMaterial, requestResult] = usePostDirectSaleMaterialRequestMutation();

  const [nonBindingMaterial, nonBindingResult] = usePostDirectSaleMaterialNonBindingMutation();

  const [changeMaterialBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchDirectSaleMaterialBasketItemQuantityMutation();

  const [deleteMaterialItems, {isLoading: isDeletingBasketItems}] =
    useBulkDeleteDirectSaleItemsMutation();

  const [, {isLoading: isEditingMaterialItem}] = usePatchDirectSaleMaterialItemMutation({
    fixedCacheKey: 'edit-direct-sale-material-item',
  });

  const basketItems = basket?.sparePartsBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.sparePartsBasketTotalPrice;

  const basketMechanicId = basket?.assignMechanicId;

  const isAddingItemsToBasket =
    dispatchResult.isLoading || requestResult.isLoading || nonBindingResult.isLoading;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isDiscardingBasket ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isEditingMaterialItem ||
    isDeletingBasketItems;

  const {setInvalidBasketItemId, hasInvalidBasketItems} = useInvalidBasketItemsIds(basketItems);

  const {mechanics, areMechanicsLoading, mechanicPayload, inputMechanicId, handleMechanicChange} =
    useBasketMechanic({
      authorizationProfileId: props.authorizationProfileId,
      assignedMechanicId: basketMechanicId,
    });

  const invalidateDocumentsCount = () => {
    // We need to wait for the document count to be updated through kafka between metada and php
    setTimeout(() => {
      dispatch(documentContextApi.util.invalidateTags(['documentsCount']));
    }, 1000);
  };

  const handleCheckout = async () => {
    if (isNotNil(mechanicPayload)) {
      return await checkoutMaterialBasket({
        directSaleId: props.directSaleId,
        body: {
          assignMechanics: mechanicPayload,
        },
      })
        .unwrap()
        .then(invalidateDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded'))
        )
        .then(props.onDiscard)
        .catch(handleApiError);
    }

    await checkoutMaterialBasket({
      directSaleId: props.directSaleId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded')))
      .then(invalidateDocumentsCount)
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleDiscard = async () => {
    await discardBasket({directSaleId: props.directSaleId})
      .unwrap()
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleWarehouseDispatchMaterial = async (warehouseArticleId: string) => {
    await dispatchMaterial({directSaleId: props.directSaleId, body: {warehouseArticleId}})
      .unwrap()
      .catch(handleApiError);
  };

  const handleWarehouseRequestMaterial = async (warehouseArticleId: string) => {
    await requestMaterial({directSaleId: props.directSaleId, body: {warehouseArticleId}})
      .unwrap()
      .catch(handleApiError);
  };

  const handleWarehouseNonBindingMaterial = async (warehouseArticleId: string) => {
    await nonBindingMaterial({directSaleId: props.directSaleId, body: {warehouseArticleId}})
      .unwrap()
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setInvalidBasketItemId(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await changeMaterialBasketItemQuantity({
      directSaleId: props.directSaleId,
      itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = async (editingDetails: MaterialBasketItemEditingDetails) => {
    if (isNil(editingDetails?.itemId)) {
      return logError("Missing itemId, can't open editing dialog");
    }

    const materialItem = (await getDirectSaleMaterialItem({
      itemId: editingDetails.itemId,
      directSaleId: props.directSaleId,
    })
      .unwrap()
      .catch(handleApiError)) as GetDirectSaleMaterialItemResponse;

    const itemName = materialItem ? `${materialItem?.name} (${materialItem?.number})` : '';

    openDialog(
      <RequestTabMaterialItemEdit
        directSaleId={props.directSaleId}
        item={materialItem}
        isEditingDisabled={false}
        authorizationProfileId={props.authorizationProfileId}
        onClose={closeCurrentDialog}
        data-testid={suffixTestId('dialog.editMaterial', props)}
      />,
      {
        title: itemName,
      }
    );
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {items: ids.map((id) => ({id}))};

    await deleteMaterialItems({
      directSaleId: props.directSaleId,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: hasPendingRequest,
      isDisabled: isBasketEmpty || hasInvalidBasketItems,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal headline={i18n.t('general.actions.addMaterial')} actions={actions}>
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <RequestTabMaterialList
              hasPendingRequest={hasPendingRequest}
              onWarehouseDispatch={handleWarehouseDispatchMaterial}
              onWarehouseRequest={handleWarehouseRequestMaterial}
              onWarehouseNonBinding={handleWarehouseNonBindingMaterial}
              availableWarehouses={props.availableWarehouses}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesMaterialBasket<BaseDirectSaleMaterialItem>
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasPendingRequest,
                hasError: hasBasketError,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{
                mechanics,
                areMechanicsLoading,
                selectedMechanicId: inputMechanicId,
                onMechanicChange: handleMechanicChange,
              }}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
